// file: client/src/writer/components/ChapterListPanel.tsx

import React from 'react';
import { ProjectState } from '../types/project';
import { Chapter, LoadingStates } from '../types';
import { actionButtonClass } 
    from '../constants';

interface ChapterListPanelProps {
    projectState: ProjectState;
    onSelectChapter: (chapterId: string) => void;
    onOpenSplitChapterModal: (chapter: Chapter) => void;
    isLoading: LoadingStates;
    selectedChapterIdsForMerge: string[];
    onToggleChapterForMerge: (chapterId: string) => void;
    isChapterSelectionDisabledForMerge: (chapterId: string) => boolean;
    canMergeSelectedChapters: boolean; 
    onOpenMergeModal: () => void;
    isInitialBlueprintReady: boolean; // Added to control messages
}

const ChapterListPanel: React.FC<ChapterListPanelProps> = ({
    projectState, 
    onSelectChapter, 
    onOpenSplitChapterModal, isLoading,
    selectedChapterIdsForMerge, onToggleChapterForMerge, isChapterSelectionDisabledForMerge,
    canMergeSelectedChapters, onOpenMergeModal, isInitialBlueprintReady
}) => {
    const chaptersExist = projectState.chapters.length > 0;
    const generatingChapters = !!isLoading['chaptersGen'];
    const generatingInitialBlueprint = !!isLoading['initialBlueprintGen'];

    return (
        <div className="h-full flex flex-col"> 
            {/* Merge Action Button */}
            {chaptersExist && (
                <div className="mb-4">
                    <button 
                        onClick={onOpenMergeModal} 
                        disabled={!canMergeSelectedChapters || generatingChapters || generatingInitialBlueprint}
                        className={`w-full py-3 px-4 text-sm font-medium rounded-lg transition-all duration-200 ${
                            canMergeSelectedChapters 
                                ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-md hover:shadow-lg' 
                                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        }`}
                        title={canMergeSelectedChapters ? "Merge selected sequential chapters" : "Select 2 or more sequential chapters to merge"}
                    >
                        {isLoading['mergeChapter'] ? (
                            <div className="flex items-center justify-center space-x-2">
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                <span>Merging...</span>
                            </div>
                        ) : (
                            `Merge Selected (${selectedChapterIdsForMerge.length})`
                        )}
                    </button>
                </div>
            )}

            {!chaptersExist && !generatingChapters && !generatingInitialBlueprint && isInitialBlueprintReady && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-blue-800 text-sm font-medium mb-2">No chapters yet</p>
                    <p className="text-blue-600 text-xs">Click "Generate Chapter Outlines" to create chapters based on your Book Details and World Building.</p>
                </div>
            )}
            {!chaptersExist && !generatingChapters && !generatingInitialBlueprint && !isInitialBlueprintReady && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-amber-800 text-sm font-medium mb-2">Blueprint Required</p>
                    <p className="text-amber-600 text-xs">Please generate an initial blueprint from the 'Project Idea' tab first.</p>
                </div>
            )}
            {generatingInitialBlueprint && (
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 border-2 border-gray-600 border-t-transparent rounded-full animate-spin"></div>
                        <p className="text-gray-700 text-sm">Generating initial project blueprint...</p>
                    </div>
                </div>
            )}
            {generatingChapters && (
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 border-2 border-gray-600 border-t-transparent rounded-full animate-spin"></div>
                        <p className="text-gray-700 text-sm">Generating chapter outlines...</p>
                    </div>
                </div>
            )}
            
            <div className="space-y-2 flex-grow overflow-y-auto">
                {projectState.chapters.map((chapter, index) => (
                    <div key={chapter.id} 
                        className={`relative p-4 rounded-xl border transition-all duration-200 cursor-pointer group ${
                            projectState.activeChapterId === chapter.id 
                                ? 'border-blue-500 bg-blue-50 shadow-lg ring-2 ring-blue-200' 
                                : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-md'
                        }`}
                        onClick={() => !(generatingChapters || generatingInitialBlueprint) && onSelectChapter(chapter.id)}
                    >
                        {/* Chapter Status Indicator */}
                        <div className={`absolute top-3 right-3 w-2 h-2 rounded-full ${
                            chapter.status === 'finalized' ? 'bg-green-500' :
                            chapter.status === 'reviewed' ? 'bg-amber-500' :
                            'bg-gray-300'
                        }`} title={`Status: ${chapter.status || 'draft'}`}></div>
                        
                        {/* Merge Checkbox */}
                        <div className="flex items-start space-x-3">
                            <input 
                                type="checkbox"
                                id={`merge-ch-${chapter.id}`}
                                className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                                checked={selectedChapterIdsForMerge.includes(chapter.id)}
                                onChange={(e) => {
                                    e.stopPropagation();
                                    onToggleChapterForMerge(chapter.id);
                                }}
                                disabled={isChapterSelectionDisabledForMerge(chapter.id) || generatingChapters || generatingInitialBlueprint}
                                aria-label={`Select chapter ${index + 1} for merge`}
                            />
                            <div className="flex-grow min-w-0">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                                        Chapter {index + 1}
                                    </span>
                                    <span className="text-xs text-gray-400">
                                        {chapter.expectedWordCount ? `${chapter.expectedWordCount} words` : 'No word count'}
                                    </span>
                                </div>
                                <h4 className={`font-semibold text-sm truncate ${
                                    projectState.activeChapterId === chapter.id ? 'text-blue-900' : 'text-gray-900'
                                }`}>
                                    {chapter.title || 'Untitled Chapter'}
                                </h4>
                            </div>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex justify-end mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button 
                                onClick={(e) => { 
                                    e.stopPropagation(); 
                                    onOpenSplitChapterModal(chapter);
                                }} 
                                className="text-xs px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors font-medium"
                                title="Split Chapter"
                                disabled={generatingChapters || generatingInitialBlueprint}
                            >
                                Split
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ChapterListPanel;